// Billing: per-exam subscriptions. Each exam shows its live entitlement status
// (from subscriptions/{uid}.exams, the same record the server gate consults).
// Subscribe routes to Stripe Checkout for that exam; one portal button manages
// all of the user's exam subscriptions at once. Entitlement is written only by
// the webhook, so what you see here is authoritative-as-read.

import { useState } from 'react';
import { useExam } from '../context/ExamContext';
import { useSubscription } from '../context/SubscriptionContext';
import { createCheckoutSession, createPortalSession } from '../lib/functions';

function statusBadge(status) {
  if (status === 'active') return 'badge badge-active';
  if (status === 'past_due') return 'badge badge-pending';
  return 'badge badge-inactive';
}

export default function Billing() {
  const { exams, loading: examsLoading } = useExam();
  const { entitlements, hasBilling, loading } = useSubscription();
  const [busy, setBusy] = useState(null); // examId | 'portal'
  const [err, setErr] = useState(null);

  async function subscribe(examId) {
    setBusy(examId);
    setErr(null);
    try {
      const url = await createCheckoutSession(examId);
      window.location.assign(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not reach Stripe.');
      setBusy(null);
    }
  }

  async function openPortal() {
    setBusy('portal');
    setErr(null);
    try {
      const url = await createPortalSession();
      window.location.assign(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not reach Stripe.');
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <span className="eyebrow">Billing</span>
        <h1>Subscriptions</h1>
        <p>Each exam is its own subscription. Subscribe to the exams you're studying.</p>
      </div>

      {err && (
        <div className="error-note" style={{ marginBottom: '1.25rem' }}>
          {err}
        </div>
      )}

      <div className="card">
        <div className="row between">
          <div className="panel-title" style={{ margin: 0 }}>
            Your exams
          </div>
          {hasBilling && (
            <button className="btn btn-sm" onClick={openPortal} disabled={busy !== null}>
              {busy === 'portal' ? 'Opening…' : 'Manage in Stripe'}
            </button>
          )}
        </div>

        {examsLoading || loading ? (
          <div className="muted" style={{ marginTop: '1rem' }}>
            Loading…
          </div>
        ) : exams.length === 0 ? (
          <div className="empty" style={{ marginTop: '1rem' }}>
            No exams are available yet.
          </div>
        ) : (
          <div className="stack" style={{ marginTop: '1rem' }}>
            {exams.map((ex) => {
              const status = entitlements?.[ex.id]?.status ?? 'inactive';
              const active = status === 'active';
              return (
                <div key={ex.id} className="row between wrap exam-billing-row">
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <strong>{ex.name}</strong>
                    <div className="row" style={{ marginTop: '0.3rem' }}>
                      <span className={statusBadge(status)}>{status}</span>
                    </div>
                  </div>
                  {active ? (
                    <span className="muted" style={{ fontSize: '0.85rem' }}>
                      Active — manage in Stripe
                    </span>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={() => subscribe(ex.id)}
                      disabled={busy !== null}
                    >
                      {busy === ex.id ? 'Starting…' : 'Subscribe'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="explanation" style={{ marginTop: '1.25rem' }}>
          <p className="muted" style={{ margin: 0, fontSize: '0.86rem' }}>
            Access is enforced server-side, per exam. Generation calls verify your subscription to
            that specific exam before doing any work. Cancelling in the Stripe portal flips the exam
            to inactive via webhook, and its generation locks immediately.
          </p>
        </div>
      </div>
    </>
  );
}
